import { gql, type TypedDocumentNode } from '@apollo/client';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import { HeadlineS, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';

import type { LeagueCollectionPreviewHeader_cardCollection } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal)`
  height: 100%;
  overflow: hidden;
`;
const ClubLogo = styled.img`
  height: 32px;
  object-fit: contain;
`;

const Name = styled(HeadlineS)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RightContent = styled(Horizontal)`
  flex: 1;
  overflow: hidden;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const StyledCircularProgress = styled(CircularProgress)`
  width: 12px;
  height: 12px;
`;

type Props = {
  cardCollection: Nullable<LeagueCollectionPreviewHeader_cardCollection>;
};

export const LeagueCollectionPreviewHeader = ({ cardCollection }: Props) => {
  const { anyTeam, userCardCollection } = cardCollection || {};
  const { pictureUrl, shortName, styleGuideColor } = anyTeam || {};

  const fulfilledSlotsCount = userCardCollection?.fulfilledSlotsCount ?? 0;
  const slotsCount = userCardCollection?.slotsCount ?? 1;
  return (
    <Root>
      {!!pictureUrl && <ClubLogo src={pictureUrl} alt="" />}
      <RightContent gap={0.5}>
        <Name>{shortName}</Name>
        <Horizontal>
          <Horizontal gap={0.5} style={{ alignItems: 'baseline' }}>
            <LabelM bold>{fulfilledSlotsCount}</LabelM>{' '}
            <Ellipsis>
              <LabelS>/ {slotsCount}</LabelS>
            </Ellipsis>
          </Horizontal>
          <StyledCircularProgress
            progress={fulfilledSlotsCount}
            maxProgress={slotsCount}
            style={
              {
                '--circular-progress-stroke': styleGuideColor,
              } as CSSProperties
            }
          />
        </Horizontal>
      </RightContent>
    </Root>
  );
};

LeagueCollectionPreviewHeader.fragments = {
  cardCollection: gql`
    fragment LeagueCollectionPreviewHeader_cardCollection on CardCollection {
      slug
      anyTeam {
        slug
        shortName
        pictureUrl(derivative: "avatar")
        styleGuideColor
      }
      userCardCollection(forUserSlug: $user) {
        id
        slotsCount
        fulfilledSlotsCount
      }
    }
  ` as TypedDocumentNode<LeagueCollectionPreviewHeader_cardCollection>,
};
